// PINBALL PANIC (§6.3): the neon table (walkable polygon with an open drain), rails with open gaps,
// slingshot rails, five pop bumpers, two flippers that fire on the beat, and the giant chrome
// pinball that loops the table from Ramp (a second one half a loop behind in Sudden Death).
// Everything moving is a pure function of the round clock, nothing is replicated:
//  - flippers fire on every 2-bar line (every bar in Sudden Death), lights blinking 2 beats ahead;
//  - the balls are locked to the beat: one loop takes 3 bars (≈ 7.7 m/s, ≈ 8.3 m/s at the SD
//    tempo) and each pass of the plunger lane lands on a downbeat; ball 1 rides 6 beats behind.
// Contact responses go through `hazardKick`: full hits are server-only, and the local bean's
// velocity is predicted (§18.2), so a bumper or flipper launch feels instant online.
import { TICK_HZ, TUNE, VOID } from '../constants';
import { EV, HazardKind, emit } from '../events';
import { Cause, MapId } from '../ids';
import { DEG, clamp } from '../math';
import { isPredicting } from '../sim/world';
import type { Vec2, World } from '../types';
import { MAP_TEMPO, beatAtTick, createTempo, roundTempo, tickAtBeat, tpbAt, type Tempo } from './clock';
import { addTelegraph, capsuleSolid, circleSolid, nearSolid, neutralHazardPose, progress, solidDistance } from './common';
import { createPolygon, inPolygon, nearestOnPolygon } from './geometry';
import { HazardKey, emitFire, emitWarn, hazardKick, hitEdgeBeans } from './hazards';
import { SolidTag, type HazardPose, type MapDef, type Solid } from './mapTypes';
import { datan2, dcos, dsin } from '../sim/dmath';

export const TUNING = {
  table: [{ x: -9, y: -6 }, { x: 9, y: -6 }, { x: 9, y: 3.5 }, { x: 3.2, y: 6 }, { x: -3.2, y: 6 }, { x: -9, y: 3.5 }],
  rail: { radius: 0.15, top: 0.6, restitution: 0.5 },
  /** Gap half-widths: top rail around x = 0, side rails around y = 0. */
  topGapHalf: 1.5,
  sideGapHalf: 1.25,
  /** Slingshot rails kick bodies back into play (no tumble: a save, not an attack). */
  slingshot: { kick: 6, cooldownTicks: 12 },
  bumper: {
    radius: 0.8, top: 1.5, positions: [{ x: -4.5, y: -2.2 }, { x: 0, y: -2.6 }, { x: 4.5, y: -2.2 }, { x: -2.2, y: 0.6 }, { x: 2.2, y: 0.6 }],
    /** Radial launch `10 · dizzyMult`, +8 Dizzy, at most once per 12 ticks per bumper and body. */
    launch: 10, dizzy: 8, cooldownTicks: 12,
  },
  flipper: {
    radius: 0.22, length: 2.4, top: 0.4, restDeg: 28, upDeg: 25, pivots: [{ x: -3, y: 5 }, { x: 3, y: 5 }],
    /** Fire cycle: up over 5 ticks, hold 6, return over 14. */
    upTicks: 5, holdTicks: 6, returnTicks: 14,
    /** Fires on every `everyBars` bar line (from BONK), every `everyBarsSD` in Sudden Death; lights blink 2 beats ahead. */
    everyBars: 2, everyBarsSD: 1, warnBeats: 2,
    /** Launch speed `clamp(|surface v| + add, min, max)` along the up-inward normal, +6 Dizzy, no Dizzy mult. */
    launchAdd: 4, launchMin: 12, launchMax: 18, dizzy: 6,
  },
  ball: {
    radius: 1.2, top: 2.4,
    /** Closed loop from the plunger lane, about 44.7 m. */
    path: [{ x: 7.5, y: 2.5 }, { x: 7.5, y: -4.7 }, { x: -7.5, y: -4.7 }, { x: -7.5, y: 2.8 }, { x: 3.5, y: 2.8 }],
    /** One loop per `loopBeats` beats (3 bars ≈ 5.8 s ≈ the §6.3 8 m/s); ball 1 rides `secondBallBeats` behind. */
    loopBeats: 12, secondBallBeats: 6,
    launchWarnBars: 1,
    /** Contact: `12 · dizzyMult` away from the ball center blended 30% with its direction, +15 Dizzy. */
    kick: 12, velBlend: 0.3, dizzy: 15, cooldownTicks: 30,
  },
  /**
   * Spawns ≥ 2.5 m from every table edge, ≥ 1.5 m from bumper surfaces, slingshots and the
   * flippers' whole swing, and ≥ 2 m apart (§29.4). (±6, 1.2) is §6.3's; its (±6, −3.4) and
   * (±1.2, −0.6) sat 1.1 m and 0.8 m from a bumper and (±3.2, 3.2) 1.49 m from the raised flipper,
   * so they moved (WP2 decisions).
   */
  spawns: [
    { x: -6.5, y: -3.5 }, { x: 6.5, y: -3.5 }, { x: -6, y: 1.2 }, { x: 6, y: 1.2 },
    { x: -2.25, y: -3.5 }, { x: 2.25, y: -3.5 }, { x: -3.3, y: 3.1 }, { x: 3.3, y: 3.1 },
  ],
  danger: { ballPad: 0.3, ballStepTicks: 3, flipper: 0.85, bumperPad: 0.3, bumper: 0.3 },
} as const;

const TABLE = createPolygon(TUNING.table);
const [TL, TR, BR, SR, SL, BL] = TUNING.table;
const RAIL = TUNING.rail;
const FLIP = TUNING.flipper;
const BALL = TUNING.ball;
const BUMP = TUNING.bumper;
const BEATS_PER_BAR = TUNE.clock.beatsPerBar;
/** Full swing from rest to up (53°). */
export const FLIPPER_SWING = (FLIP.restDeg + FLIP.upDeg) * DEG;
const CYCLE_TICKS = FLIP.upTicks + FLIP.holdTicks + FLIP.returnTicks;

function rail(ax: number, ay: number, bx: number, by: number, tag: number, index: number): Solid {
  return capsuleSolid(ax, ay, bx, by, RAIL.radius, RAIL.top, RAIL.restitution, tag, index);
}

const STATIC_SOLIDS: readonly Solid[] = [
  rail(TL.x, TL.y, -TUNING.topGapHalf, TL.y, SolidTag.NONE, 0),
  rail(TUNING.topGapHalf, TR.y, TR.x, TR.y, SolidTag.NONE, 1),
  rail(TL.x, TL.y, TL.x, -TUNING.sideGapHalf, SolidTag.NONE, 2),
  rail(BL.x, TUNING.sideGapHalf, BL.x, BL.y, SolidTag.NONE, 3),
  rail(TR.x, TR.y, TR.x, -TUNING.sideGapHalf, SolidTag.NONE, 4),
  rail(BR.x, TUNING.sideGapHalf, BR.x, BR.y, SolidTag.NONE, 5),
  rail(BL.x, BL.y, SL.x, SL.y, SolidTag.SLINGSHOT, 0),
  rail(BR.x, BR.y, SR.x, SR.y, SolidTag.SLINGSHOT, 1),
  ...BUMP.positions.map((p, i) => circleSolid(p.x, p.y, BUMP.radius, BUMP.top, TUNE.body.restitution, SolidTag.BUMPER, i)),
];
const FLIPPER_SOLIDS: Solid[] = [0, 1].map((i) => capsuleSolid(0, 0, 0, 0, FLIP.radius, FLIP.top, TUNE.body.restitution, SolidTag.FLIPPER, i));
const BALL_SOLIDS: Solid[] = [0, 1].map((i) => circleSolid(0, 0, BALL.radius, BALL.top, TUNE.body.restitution, SolidTag.PINBALL, i));

const tempo = createTempo();

export function pinballTempo(w: World): Tempo {
  return roundTempo(w.cfg.mapId, w.cfg.modeId, w.cfg.capTickOverride, tempo);
}

// ---- Flippers -----------------------------------------------------------------------------------

function fireEvery(b: number, t: Tempo): number {
  return (b >= t.sdBeat ? FLIP.everyBarsSD : FLIP.everyBars) * BEATS_PER_BAR;
}

/** Beat of the latest flipper fire at or before beat `b` (fires from beat 0), or −Infinity. */
function lastFireBeat(b: number, t: Tempo): number {
  if (b < 0) return -Infinity;
  if (b >= t.sdBeat) {
    const every = fireEvery(b, t);
    return t.sdBeat + Math.floor((b - t.sdBeat) / every) * every;
  }
  const every = fireEvery(b, t);
  return Math.floor(b / every) * every;
}

/** Beat of the first flipper fire strictly after beat `b`. */
function nextFireBeat(b: number, t: Tempo): number {
  if (b < 0) return 0;
  if (b >= t.sdBeat) {
    const every = fireEvery(b, t);
    return t.sdBeat + (Math.floor((b - t.sdBeat) / every) + 1) * every;
  }
  const every = fireEvery(b, t);
  return Math.min((Math.floor(b / every) + 1) * every, t.sdBeat);
}

/** Round tick of the latest flipper fire at or before `tick` (−Infinity before BONK). */
export function lastFlipperFire(tick: number, t: Tempo): number {
  const fb = lastFireBeat(beatAtTick(t, tick), t);
  return fb === -Infinity ? -Infinity : tickAtBeat(t, fb);
}

/** Round tick of the next flipper fire strictly after `tick`. */
export function nextFlipperFire(tick: number, t: Tempo): number {
  return tickAtBeat(t, nextFireBeat(beatAtTick(t, tick), t));
}

/** Tick at which the 2-beat light telegraph of the fire at `fireTick` starts. */
export function flipperWarnTick(fireTick: number, t: Tempo): number {
  return tickAtBeat(t, beatAtTick(t, fireTick) - FLIP.warnBeats);
}

/** Flipper swing from rest toward the up position (radians, 0..53°) at a fractional tick. */
export function flipperSwing(tick: number, t: Tempo): number {
  const dt = tick - lastFlipperFire(tick, t);
  if (!(dt >= 0)) return 0;
  if (dt < FLIP.upTicks) return (FLIPPER_SWING * dt) / FLIP.upTicks;
  if (dt < FLIP.upTicks + FLIP.holdTicks) return FLIPPER_SWING;
  if (dt < CYCLE_TICKS) return FLIPPER_SWING * (1 - (dt - FLIP.upTicks - FLIP.holdTicks) / FLIP.returnTicks);
  return 0;
}

/** Rate of the swing (rad/s, + while swinging up) at `tick`. */
function swingRate(tick: number, t: Tempo): number {
  const dt = tick - lastFlipperFire(tick, t);
  if (!(dt >= 0)) return 0;
  if (dt < FLIP.upTicks) return (FLIPPER_SWING / FLIP.upTicks) * TICK_HZ;
  if (dt < FLIP.upTicks + FLIP.holdTicks) return 0;
  if (dt < CYCLE_TICKS) return -(FLIPPER_SWING / FLIP.returnTicks) * TICK_HZ;
  return 0;
}

/** True during the 5-tick upswing that launches bodies. */
export function flipperUpswing(tick: number, t: Tempo): boolean {
  const dt = tick - lastFlipperFire(tick, t);
  return dt >= 0 && dt < FLIP.upTicks;
}

/** Direction angle of flipper `i` (0 left, 1 right) for a given swing. */
export function flipperDirAngle(i: number, swing: number): number {
  const rest = FLIP.restDeg * DEG;
  return i === 0 ? rest - swing : Math.PI - rest + swing;
}

/** The up-inward unit normal of flipper `i` at direction angle `a`. */
function upNormal(i: number, a: number, out: Vec2): Vec2 {
  out.x = i === 0 ? dsin(a) : -dsin(a);
  out.y = i === 0 ? -dcos(a) : dcos(a);
  return out;
}

function setFlipperSolid(i: number, tick: number, t: Tempo): Solid {
  const s = FLIPPER_SOLIDS[i];
  const p = FLIP.pivots[i];
  const a = flipperDirAngle(i, flipperSwing(tick, t));
  s.ax = p.x;
  s.ay = p.y;
  s.bx = p.x + dcos(a) * FLIP.length;
  s.by = p.y + dsin(a) * FLIP.length;
  s.cx = p.x;
  s.cy = p.y;
  // The left flipper swings up with a decreasing angle, the right one with an increasing angle.
  s.omega = (i === 0 ? -1 : 1) * swingRate(tick, t);
  return s;
}

// ---- Giant pinballs -----------------------------------------------------------------------------

const SEG_LEN: number[] = BALL.path.map((p, i) => {
  const q = BALL.path[(i + 1) % BALL.path.length];
  return Math.hypot(q.x - p.x, q.y - p.y);
});
/** Loop length in meters (about 44.7). */
export const BALL_LOOP_LENGTH = SEG_LEN.reduce((a, b) => a + b, 0);

/** Beat at which ball `j` leaves the plunger lane: ball 0 at Ramp, ball 1 at its first lane pass in Sudden Death. */
export function ballLaunchBeat(j: number, t: Tempo): number {
  if (j === 0) return t.rampBeat;
  if (!Number.isFinite(t.sdBeat)) return Infinity;
  const first = t.rampBeat + BALL.secondBallBeats;
  return first + Math.max(0, Math.ceil((t.sdBeat - first) / BALL.loopBeats)) * BALL.loopBeats;
}

/** Round tick of ball `j`'s launch. */
export function ballLaunchTick(j: number, t: Tempo): number {
  return tickAtBeat(t, ballLaunchBeat(j, t));
}

/** Tick at which ball `j`'s 1-bar BALL LAUNCH warning starts. */
export function ballWarnTick(j: number, t: Tempo): number {
  return tickAtBeat(t, ballLaunchBeat(j, t) - BALL.launchWarnBars * BEATS_PER_BAR);
}

/** Distance ball `j` has rolled from the lane at a fractional tick (negative before its launch). */
export function ballRolled(j: number, tick: number, t: Tempo): number {
  return (BALL_LOOP_LENGTH * (beatAtTick(t, tick) - ballLaunchBeat(j, t))) / BALL.loopBeats;
}

/** Ball rolling speed in m/s at `tick` (the loop takes `loopBeats` beats of the tempo in effect). */
export function ballSpeed(tick: number, t: Tempo): number {
  return (BALL_LOOP_LENGTH / (BALL.loopBeats * tpbAt(t, tick))) * TICK_HZ;
}

/** Ball `j` is rolling (and solid) at `tick`. */
export function ballActive(j: number, tick: number, t: Tempo): boolean {
  return tick >= ballLaunchTick(j, t);
}

/** Point at arc length `s` along the loop (and the unit direction of travel). */
export function ballPathPoint(s: number, out: Vec2, dir?: Vec2): Vec2 {
  let d = ((s % BALL_LOOP_LENGTH) + BALL_LOOP_LENGTH) % BALL_LOOP_LENGTH;
  for (let i = 0; i < SEG_LEN.length; i++) {
    if (d <= SEG_LEN[i] || i === SEG_LEN.length - 1) {
      const p = BALL.path[i];
      const q = BALL.path[(i + 1) % BALL.path.length];
      const f = Math.min(1, d / SEG_LEN[i]);
      out.x = p.x + (q.x - p.x) * f;
      out.y = p.y + (q.y - p.y) * f;
      if (dir) {
        dir.x = (q.x - p.x) / SEG_LEN[i];
        dir.y = (q.y - p.y) / SEG_LEN[i];
      }
      return out;
    }
    d -= SEG_LEN[i];
  }
  return out;
}

const dirScratch: Vec2 = { x: 0, y: 0 };
const ballScratch: Vec2 = { x: 0, y: 0 };

function setBallSolid(j: number, tick: number, t: Tempo): Solid {
  const s = BALL_SOLIDS[j];
  ballPathPoint(ballRolled(j, tick, t), ballScratch, dirScratch);
  const v = ballSpeed(tick, t);
  s.ax = ballScratch.x;
  s.ay = ballScratch.y;
  s.vx = dirScratch.x * v;
  s.vy = dirScratch.y * v;
  return s;
}

// ---- Pose, events, contact ----------------------------------------------------------------------

const pt: Vec2 = { x: 0, y: 0 };
const nrm: Vec2 = { x: 0, y: 0 };
const LANE = BALL.path[0];

function writePose(w: World, tick: number, out: HazardPose): HazardPose {
  neutralHazardPose(out);
  const t = pinballTempo(w);
  const swing = flipperSwing(tick, t);
  out.flipperAngles[0] = swing;
  out.flipperAngles[1] = swing;
  const fire = nextFlipperFire(tick, t);
  const warn = flipperWarnTick(fire, t);
  if (tick >= warn) {
    out.flipperWarn = progress(tick, warn, fire);
    const beat = beatAtTick(t, tick);
    out.flipperLights = beat - Math.floor(beat) < 0.5;
    addTelegraph(out, HazardKind.FLIPPER, 0, warn, fire, 0, FLIP.pivots[0].y);
  }
  for (let j = 0; j < out.balls.length; j++) {
    const b = out.balls[j];
    const launch = ballLaunchTick(j, t);
    const rolled = ballRolled(j, tick, t);
    b.active = tick >= launch;
    ballPathPoint(Math.max(0, rolled), pt);
    b.x = pt.x;
    b.y = pt.y;
    if (out.ballRoll) out.ballRoll[j] = Math.max(0, rolled);
    const bw = ballWarnTick(j, t);
    if (tick >= bw && tick < launch) {
      out.ballWarn = Math.max(out.ballWarn ?? 0, progress(tick, bw, launch));
      addTelegraph(out, HazardKind.PINBALL_LAUNCH, j, bw, launch, LANE.x, LANE.y);
    }
  }
  return out;
}

/** Time-derived events at integer tick `w.tick` (both modes): flipper lights and fires, ball launches. */
function emitTelegraphs(w: World, t: Tempo): void {
  const tick = w.tick;
  if (lastFlipperFire(tick, t) === tick) emitFire(w, HazardKind.FLIPPER, 0, 0, FLIP.pivots[0].y);
  const fire = nextFlipperFire(tick, t);
  if (Math.round(flipperWarnTick(fire, t)) === tick) emitWarn(w, HazardKind.FLIPPER, 0, fire - tick, 0, FLIP.pivots[0].y);
  for (let j = 0; j < 2; j++) {
    const launch = ballLaunchTick(j, t);
    if (launch === tick) emitFire(w, HazardKind.PINBALL_LAUNCH, j, LANE.x, LANE.y);
    else if (Math.round(ballWarnTick(j, t)) === tick) emitWarn(w, HazardKind.PINBALL_LAUNCH, j, launch - tick, LANE.x, LANE.y);
  }
}

function flipperContact(w: World, slot: number, s: Solid, nx: number, ny: number): void {
  if (!flipperUpswing(w.tick, pinballTempo(w))) return;
  const i = s.index;
  upNormal(i, datan2(s.by - s.ay, s.bx - s.ax), nrm);
  if (nx * nrm.x + ny * nrm.y <= 0) return; // underside: the flipper swings away from it
  const b = w.beans[slot];
  const cx = b.pos.x - nx * b.radius;
  const cy = b.pos.y - ny * b.radius;
  const surface = Math.abs(s.omega) * Math.hypot(cx - s.cx, cy - s.cy);
  const speed = clamp(surface + FLIP.launchAdd, FLIP.launchMin, FLIP.launchMax);
  hazardKick(w, slot, nrm.x, nrm.y, speed, { cause: Cause.FLIPPER, dizzy: FLIP.dizzy, scaleByDizzy: false, key: HazardKey.FLIPPER + i, cooldown: CYCLE_TICKS, carry: true });
}

function ballContact(w: World, slot: number, s: Solid, nx: number, ny: number): void {
  const v = Math.hypot(s.vx, s.vy) || 1;
  const k = BALL.velBlend;
  const dx = (1 - k) * nx + (k * s.vx) / v;
  const dy = (1 - k) * ny + (k * s.vy) / v;
  hazardKick(w, slot, dx, dy, BALL.kick, { cause: Cause.PINBALL, dizzy: BALL.dizzy, scaleByDizzy: true, key: HazardKey.BALL + s.index, cooldown: BALL.cooldownTicks, carry: true });
}

function bumperContact(w: World, slot: number, s: Solid, nx: number, ny: number): void {
  const opts = { cause: Cause.BUMPER, dizzy: BUMP.dizzy, scaleByDizzy: true, key: HazardKey.BUMPER + s.index, cooldown: BUMP.cooldownTicks, carry: false };
  if (hazardKick(w, slot, nx, ny, BUMP.launch, opts)) emit(w, EV.BUMPER, slot, 255, s.index, 0, s.ax, s.ay);
}

// ---- Danger and footprints ----------------------------------------------------------------------

/**
 * Ball positions over the lookahead and whether a flipper fires inside it, for one (world, tick,
 * lookahead): `danger()` is sampled on whole grids per tick and this is a pure function of the tick.
 */
const MAX_FORECAST_STEPS = 64;
const fc = {
  w: null as World | null, tick: NaN, ahead: -1, steps: 0, flipperSoon: false,
  on: new Uint8Array(2 * MAX_FORECAST_STEPS), x: new Float64Array(2 * MAX_FORECAST_STEPS), y: new Float64Array(2 * MAX_FORECAST_STEPS),
};

function forecast(w: World, aheadTicks: number): typeof fc {
  if (fc.w === w && fc.tick === w.tick && fc.ahead === aheadTicks) return fc;
  const t = pinballTempo(w);
  const step = TUNING.danger.ballStepTicks;
  fc.w = w;
  fc.tick = w.tick;
  fc.ahead = aheadTicks;
  fc.steps = Math.min(MAX_FORECAST_STEPS, Math.floor(aheadTicks / step) + 1);
  for (let j = 0; j < 2; j++) {
    for (let k = 0; k < fc.steps; k++) {
      const i = j * MAX_FORECAST_STEPS + k;
      const tick = w.tick + k * step;
      fc.on[i] = ballActive(j, tick, t) ? 1 : 0;
      if (!fc.on[i]) continue;
      ballPathPoint(ballRolled(j, tick, t), pt);
      fc.x[i] = pt.x;
      fc.y[i] = pt.y;
    }
  }
  const fire = flipperUpswing(w.tick, t) ? w.tick : nextFlipperFire(w.tick, t);
  fc.flipperSoon = fire - w.tick <= aheadTicks;
  return fc;
}

/** Static-solid danger near bumpers (they launch, which may or may not be welcome). */
function bumperDanger(x: number, y: number): number {
  for (const p of BUMP.positions) {
    if (Math.hypot(x - p.x, y - p.y) <= BUMP.radius + TUNE.bean.radius + TUNING.danger.bumperPad) return TUNING.danger.bumper;
  }
  return 0;
}

/** Flipper capsules at rest and fully up, per flipper (for the swept envelope). */
const FLIPPER_EXTREMES: readonly (readonly Solid[])[] = [0, 1].map((i) => [0, FLIPPER_SWING].map((swing) => {
  const p = FLIP.pivots[i];
  const a = flipperDirAngle(i, swing);
  return capsuleSolid(p.x, p.y, p.x + dcos(a) * FLIP.length, p.y + dsin(a) * FLIP.length, FLIP.radius, FLIP.top, 0, SolidTag.FLIPPER, i);
}));

/** Distance from (x, y) to flipper `i`'s swept area (rest to up), 0 inside it. */
function flipperSweepDistance(i: number, x: number, y: number): number {
  const p = FLIP.pivots[i];
  const d = Math.hypot(x - p.x, y - p.y);
  const a0 = flipperDirAngle(i, 0);
  const a1 = flipperDirAngle(i, FLIPPER_SWING);
  const lo = Math.min(a0, a1);
  const hi = Math.max(a0, a1);
  let a = datan2(y - p.y, x - p.x);
  while (a < lo - Math.PI) a += 2 * Math.PI;
  while (a > lo + Math.PI) a -= 2 * Math.PI;
  if (a >= lo && a <= hi) return Math.max(0, d - FLIP.length - FLIP.radius);
  let best = Infinity;
  for (const s of FLIPPER_EXTREMES[i]) best = Math.min(best, solidDistance(s, x, y));
  return Math.max(0, best);
}

/** The distance from (x, y) to the nearest flipper's full swing envelope (0 inside). */
export function flipperSweepDist(x: number, y: number): number {
  return Math.min(flipperSweepDistance(0, x, y), flipperSweepDistance(1, x, y));
}

/** True if (x, y) is within `pad` of either flipper's full swing. */
export function nearFlipperSweep(x: number, y: number, pad: number): boolean {
  return flipperSweepDist(x, y) <= pad;
}

const LOOP_TUBE: readonly Solid[] = BALL.path.map((p, i) => {
  const q = BALL.path[(i + 1) % BALL.path.length];
  return capsuleSolid(p.x, p.y, q.x, q.y, BALL.radius, BALL.top, 0, SolidTag.PINBALL, i);
});

/** Within `pad` of the ball loop's tube (crate placement, tests). */
export function nearBallLoop(x: number, y: number, pad: number): boolean {
  return nearSolid(LOOP_TUBE, x, y, pad);
}

/** The static hazard footprints (bumpers, slingshots) for spawn and crate checks. */
export const PINBALL_STATIC_SOLIDS = STATIC_SOLIDS;

export const PINBALL: MapDef = {
  id: MapId.PINBALL,
  name: 'Pinball Panic',
  tpb: MAP_TEMPO[MapId.PINBALL].tpb,
  tpbSD: MAP_TEMPO[MapId.PINBALL].tpbSD,
  bounds: { minX: TL.x, maxX: TR.x, minY: TL.y, maxY: SL.y },
  spawns: TUNING.spawns.map((p) => ({ x: p.x, y: p.y })),
  voidKind: 'grid',
  center: { x: 0, y: 0 },
  initState() {},
  groundAt(_w, x, y) {
    return inPolygon(x, y, TABLE) ? 0 : VOID;
  },
  nearestGround(_w, x, y, out) {
    return nearestOnPolygon(x, y, TABLE, out);
  },
  solids(w, out) {
    const t = pinballTempo(w);
    let n = 0;
    for (const s of STATIC_SOLIDS) out[n++] = s;
    out[n++] = setFlipperSolid(0, w.tick, t);
    out[n++] = setFlipperSolid(1, w.tick, t);
    for (let j = 0; j < 2; j++) if (ballActive(j, w.tick, t)) out[n++] = setBallSolid(j, w.tick, t);
    return n;
  },
  onSolidContact(w, id, isEntity, s, nx, ny) {
    if (isEntity) return;
    switch (s.tag) {
      case SolidTag.BUMPER:
        bumperContact(w, id, s, nx, ny);
        break;
      case SolidTag.SLINGSHOT:
        hazardKick(w, id, nx, ny, TUNING.slingshot.kick, { cause: Cause.SLINGSHOT, dizzy: 0, scaleByDizzy: false, key: HazardKey.SLINGSHOT + s.index, cooldown: TUNING.slingshot.cooldownTicks, carry: false, noTumble: true });
        break;
      case SolidTag.FLIPPER:
        flipperContact(w, id, s, nx, ny);
        break;
      case SolidTag.PINBALL:
        ballContact(w, id, s, nx, ny);
        break;
      default:
        break;
    }
  },
  step(w) {
    const t = pinballTempo(w);
    emitTelegraphs(w, t);
    if (w.tick <= 0 || isPredicting(w)) return;
    if (swingRate(w.tick, t) !== 0) {
      hitEdgeBeans(w, setFlipperSolid(0, w.tick, t), Cause.FLIPPER);
      hitEdgeBeans(w, setFlipperSolid(1, w.tick, t), Cause.FLIPPER);
    }
    for (let j = 0; j < 2; j++) if (ballActive(j, w.tick, t)) hitEdgeBeans(w, setBallSolid(j, w.tick, t), Cause.PINBALL);
  },
  danger(w, x, y, aheadTicks) {
    const f = forecast(w, aheadTicks);
    let d = bumperDanger(x, y);
    const reach2 = (BALL.radius + TUNE.bean.radius + TUNING.danger.ballPad) ** 2;
    const step = TUNING.danger.ballStepTicks;
    for (let j = 0; j < 2; j++) {
      for (let k = 0; k < f.steps; k++) {
        const i = j * MAX_FORECAST_STEPS + k;
        if (!f.on[i]) continue;
        if ((x - f.x[i]) ** 2 + (y - f.y[i]) ** 2 <= reach2) {
          d = Math.max(d, 1 - (k * step) / (aheadTicks + step));
          break;
        }
      }
    }
    if (f.flipperSoon && nearFlipperSweep(x, y, TUNE.bean.radius)) d = Math.max(d, TUNING.danger.flipper);
    return d;
  },
  hazardPose(w, tick, out) {
    return writePose(w, tick, out);
  },
  crateExclusion(_w, x, y) {
    const pad = TUNE.crate.radius;
    return nearSolid(STATIC_SOLIDS, x, y, pad) || nearFlipperSweep(x, y, pad) || nearBallLoop(x, y, pad);
  },
};
